import React, { useState, useRef } from 'react';
import axios from 'axios';

interface TTSResult {
  audioUrl: string;
  latencyMs: number;
  audioSizeBytes: number;
  costEstimate: number;
}

/**
 * OpenAI TTS-1 Prototype
 * Tests OpenAI's TTS-1 API for premium neural voices
 *
 * Features to test:
 * - Voice quality vs Web Speech API
 * - Available voices (alloy, echo, fable, onyx, nova, shimmer)
 * - Speed control (0.25x - 4.0x)
 * - Latency (API call + download time)
 * - Audio file size (for bandwidth estimates)
 * - Cost per character ($15/1M characters)
 *
 * Backend endpoint required: POST /api/app/tts-prototype/openai
 */
export const OpenAITTSPrototype: React.FC = () => {
  const [text, setText] = useState(
    "OpenAI's text-to-speech API is powered by our TTS model. It offers six built-in voices and can be used to narrate a written blog post, produce spoken audio in multiple languages, give real time audio output using streaming, and more. The audio quality won't be as good as what a professional voice actor could provide, but it's sufficient for many applications."
  );
  const [voice, setVoice] = useState<string>('alloy');
  const [speed, setSpeed] = useState<number>(1.0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState<TTSResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const voices = [
    { id: 'alloy', name: 'Alloy', description: 'Neutral, balanced' },
    { id: 'echo', name: 'Echo', description: 'Male, authoritative' },
    { id: 'fable', name: 'Fable', description: 'British, narrative' },
    { id: 'onyx', name: 'Onyx', description: 'Deep male, strong' },
    { id: 'nova', name: 'Nova', description: 'Female, energetic' },
    { id: 'shimmer', name: 'Shimmer', description: 'Soft female, warm' }
  ];

  const handleGenerate = async () => {
    if (!text.trim()) {
      setError('Please enter text to convert');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setResult(null);

    const startTime = performance.now();

    try {
      // Call backend API (which will call OpenAI TTS-1 API)
      // Note: For testing, use proxy server: http://localhost:3003
      const apiBaseUrl = import.meta.env.VITE_TTS_PROXY_URL || 'http://localhost:3003';
      const response = await axios.post<TTSResult>(
        `${apiBaseUrl}/api/app/tts-prototype/openai`,
        {
          text,
          voice,
          speed
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const endTime = performance.now();
      const totalLatency = endTime - startTime;

      setResult({
        ...response.data,
        latencyMs: totalLatency // Total latency (API call + download)
      });

      console.log('🎤 OpenAI TTS-1 generated successfully:', response.data);
    } catch (err: any) {
      console.error('🎤 OpenAI TTS-1 error:', err);
      if (err.code === 'ERR_NETWORK' || err.message.includes('Network Error')) {
        setError('Cannot connect to proxy server. Make sure proxy is running on http://localhost:3003 (run: node tts-proxy-server.js)');
      } else if (err.response?.status === 404) {
        setError('Proxy server endpoint not found. Make sure tts-proxy-server.js is running.');
      } else if (err.response?.data?.error) {
        setError(`Error: ${err.response.data.error}`);
      } else if (err.response?.data?.error?.message) {
        setError(`OpenAI API Error: ${err.response.data.error.message}`);
      } else {
        setError(`Error: ${err.message || 'Failed to generate speech'}`);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePlay = () => {
    if (result && audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const costPer1MChars = 15.00;
  const estimatedCost = (text.length / 1_000_000) * costPer1MChars;

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2 text-gray-900 dark:text-white">
          OpenAI TTS-1 Prototype
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Test OpenAI's premium neural TTS ($15/1M characters)
        </p>
      </div>

      {/* Voice Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Voice (6 premium neural voices)
        </label>
        <select
          value={voice}
          onChange={(e) => setVoice(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          disabled={isGenerating}
        >
          {voices.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name} - {v.description}
            </option>
          ))}
        </select>
      </div>

      {/* Text Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Text to speak ({text.length} characters)
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="w-full h-32 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
          placeholder="Enter text to convert to speech..."
          disabled={isGenerating}
        />
      </div>

      {/* Speed Control */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Speed: {speed.toFixed(2)}x
        </label>
        <input
          type="range"
          min="0.25"
          max="4.0"
          step="0.25"
          value={speed}
          onChange={(e) => setSpeed(parseFloat(e.target.value))}
          className="w-full"
          disabled={isGenerating}
        />
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
          <span>0.25x (Very Slow)</span>
          <span>1.0x (Normal)</span>
          <span>4.0x (Very Fast)</span>
        </div>
      </div>

      {/* Cost Estimate */}
      <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
        <p className="text-sm font-medium text-yellow-900 dark:text-yellow-300">
          💰 Estimated cost: <span className="font-bold">${estimatedCost.toFixed(6)}</span>
        </p>
        <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
          Based on $15.00 per 1M characters. Current text: {text.length} chars.
        </p>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={isGenerating || !text.trim()}
        className="w-full px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors mb-6"
      >
        {isGenerating ? '⏳ Generating...' : '🚀 Generate Speech (OpenAI TTS-1)'}
      </button>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <p className="text-sm font-medium text-red-900 dark:text-red-300">
            ❌ {error}
          </p>
        </div>
      )}

      {/* Results */}
      {result && (
        <>
          {/* Latency & Stats */}
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <h3 className="font-bold text-sm mb-3 text-blue-900 dark:text-blue-300">
              📊 Generation Stats
            </h3>
            <div className="space-y-2 text-sm text-blue-800 dark:text-blue-400">
              <div className="flex justify-between">
                <span>⚡ Total latency (API + download):</span>
                <span className="font-bold">{result.latencyMs.toFixed(0)}ms</span>
              </div>
              <div className="flex justify-between">
                <span>📦 Audio file size:</span>
                <span className="font-bold">{(result.audioSizeBytes / 1024).toFixed(1)} KB</span>
              </div>
              <div className="flex justify-between">
                <span>💵 Actual cost:</span>
                <span className="font-bold">${result.costEstimate.toFixed(6)}</span>
              </div>
              <div className="flex justify-between">
                <span>✅ Quality assessment:</span>
                <span className="font-bold">
                  {result.latencyMs < 1000 ? '🟢 Excellent' :
                   result.latencyMs < 3000 ? '🟡 Good' :
                   '🔴 Slow'}
                </span>
              </div>
            </div>
          </div>

          {/* Audio Player */}
          <div className="mb-6">
            <audio
              ref={audioRef}
              src={result.audioUrl}
              onEnded={() => setIsPlaying(false)}
              className="w-full mb-4"
              controls
            />

            <div className="flex gap-3">
              <button
                onClick={handlePlay}
                disabled={isPlaying}
                className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
              >
                ▶️ Play
              </button>
              <button
                onClick={handlePause}
                disabled={!isPlaying}
                className="flex-1 px-6 py-3 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
              >
                ⏸️ Pause
              </button>
              <button
                onClick={handleStop}
                className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
              >
                ⏹️ Stop
              </button>
            </div>
          </div>
        </>
      )}

      {/* Testing Checklist */}
      <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-6 bg-gray-50 dark:bg-gray-900">
        <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">
          📋 Testing Checklist
        </h3>
        <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Test all 6 voices - Compare naturalness</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Compare voice quality vs Web Speech API</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Test on iPhone 16 Pro - Measure latency</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Test on Google Pixel 9 - Measure latency</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Test on iPad - Verify playback quality</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Test speed variations (0.5x, 1.0x, 1.5x, 2.0x)</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Measure bandwidth usage (audio file size)</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Rate voice naturalness (1-10 scale) vs Web Speech</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Calculate real-world cost for typical chapter (5,000 words)</label>
          </div>
        </div>
      </div>

      {/* Implementation Note */}
      <div className="mt-6 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
        <h4 className="font-bold text-sm mb-2 text-orange-900 dark:text-orange-300">
          ⚠️ Backend Implementation Required
        </h4>
        <p className="text-xs text-orange-700 dark:text-orange-400 mb-2">
          This prototype requires a backend endpoint to securely call the OpenAI API (API key must not be exposed to frontend).
        </p>
        <div className="text-xs text-orange-800 dark:text-orange-500 font-mono bg-white dark:bg-gray-900 p-2 rounded">
          POST /api/app/tts-prototype/openai<br/>
          Request: &#123; text: string, voice: string, speed: number &#125;<br/>
          Response: &#123; audioUrl: string, audioSizeBytes: number, costEstimate: number &#125;
        </div>
      </div>
    </div>
  );
};

export default OpenAITTSPrototype;
